import { IPage, registerPage } from '../Components/Page';
import Home from './Home';
import { ReactComponent as SettingsIcon } from '../Images/settings.svg';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, FormControl, InputLabel, MenuItem, Select, Switch, TextField } from '@mui/material';
import { IUserSettings, userSettingsStore } from '../Services/UserSettingsStore';
import { useOperation } from '../Hooks/OperationContext';
import { useLocation } from 'react-router';
import session from '../Services/Session';
import { appSettings } from '../AppSettings';
import "./Settings.scss"
import { vibrate } from '../Services/SoundPlayer';
import { isNativeDevice } from '../Platform';

interface ISettingsParams { 
    mode?: "admin";
    autosave?: "1";
    pin?: string;
    apiUrl: string;
    cameraEngine: string;
    debugMode?: "1";
    scanDelay: string;
    logo?: string;
    monitorMode?: "1";
    offlineMode?: "1";
    enableScannerBLT?: "1";
    hideMenu?: "1";
}

interface ISettings extends Partial<IUserSettings> {
    pin?: string;
    deviceId?: string;
}

export function SettingsContent() {

    const operation = useOperation();

    const location = useLocation();

    const [settings, setSettings] = useState<ISettings>();

    const [settingsCode, setSettingsCode] = useState<string>();

    const curParams = useMemo(() => {

        const params = new URLSearchParams(location.search);

        const result = {} as ISettingsParams;

        for (const key of params.keys())
            result[key] = params.get(key);

        console.log(result);

        return result;

    }, [location.search]);

    const [isAdmin, setIsAdmin] = useState(curParams.mode == "admin" || appSettings.debugMode);

    const isAutoSave = curParams.autosave == "1";

    useEffect(() => {

        const newSettings: ISettings = { ...appSettings };

        if (curParams.apiUrl)
            newSettings.apiUrl = curParams.apiUrl;

        if (curParams.cameraEngine)
            newSettings.cameraEngine = curParams.cameraEngine as any;

        if (curParams.debugMode !== undefined)
            newSettings.debugMode = curParams.debugMode === "1" || curParams.debugMode === "true";

        if (curParams.hideMenu !== undefined)
            newSettings.hideMenu = curParams.hideMenu === "1" || curParams.hideMenu === "true";

        if (curParams.enableScannerBLT !== undefined)
            newSettings.enableScannerBLT = curParams.enableScannerBLT === "1" || curParams.enableScannerBLT === "true";

        if (curParams.scanDelay || curParams.scanDelay === "0")
            newSettings.scanDelay = parseInt(curParams.scanDelay as string);

        if (curParams.logo)
            newSettings.logo = curParams.logo;

        if (!newSettings.scanDelay)
            newSettings.scanDelay = 0;

        if (curParams.monitorMode !== undefined)
            newSettings.monitorMode = curParams.monitorMode == "1" || curParams.monitorMode == "true";

        if (curParams.offlineMode !== undefined)
            newSettings.offlineMode = curParams.offlineMode == "1" || curParams.offlineMode == "true";

        newSettings.pin = curParams.pin ?? session.pin;

        newSettings.deviceId = session.deviceId;

        setSettings(newSettings);
    }, []);

    useEffect(() => {

        (async () => {

            if (settings && (isAutoSave || curParams.pin))
                await saveSettingsAsync(false);

            if (curParams.pin)
                await session.openAsync(curParams.pin);

            if (isAutoSave)
                window.location.href = window.location.pathname;

        })();

    }, [settings]);

    const attachPointer = (ref: HTMLElement) => {

        if (!ref)
            return;

        let downStartTime: number;
        let downTimerId: number = null;

        ref.onselect = () => false;
        ref.oncontextmenu = () => false;

        ref.addEventListener("pointerup", () => {
            if (downTimerId !== null)
                clearTimeout(downTimerId);
            downTimerId = null;
            downStartTime = null;
        });

        ref.addEventListener("pointerdown", ev => {
            if (downTimerId !== null)
                clearTimeout(downTimerId);
            downStartTime = new Date().getTime();
            downTimerId = setTimeout(() => {
                if (!downStartTime)
                    return;
                setIsAdmin(true);
                vibrate(true);
                downStartTime = null;
                downTimerId = null;
            }, 2000) as any;

            ev.preventDefault();
            ev.stopPropagation();
            ev.stopImmediatePropagation();
            return false;
        });
    }


    const setValue = <TKey extends keyof ISettings>(name: TKey, value: ISettings[TKey], autoSave = false) => {

        const newValue = { ...settings };

        newValue[name] = value;

        if (autoSave) {
            if (name == "deviceId")
                session.updateDeviceId(value as string);
            else {
                userSettingsStore.current[name as any] = value;
                userSettingsStore.saveAsync();
            }
        }
         
        setSettings(newValue);
    }

    const saveSettingsAsync = async (reload = true) => {

        console.log("Saving", settings);

        if (isAdmin) {

            userSettingsStore.current.apiUrl = settings.apiUrl;
            userSettingsStore.current.debugMode = settings.debugMode === true;
            userSettingsStore.current.enableScannerBLT = settings.enableScannerBLT === true;
            userSettingsStore.current.cameraEngine = settings.cameraEngine;
            userSettingsStore.current.logo = settings.logo;
            userSettingsStore.current.pingTimeout = settings.pingTimeout;
            userSettingsStore.current.onlineCheckInterval = settings.onlineCheckInterval;

            if (!userSettingsStore.current.apiUrl)
                delete userSettingsStore.current.apiUrl;

            if (settings.pin)
                await session.openAsync(settings.pin);

            if (settings.deviceId)
                localStorage.setItem("DeviceId", settings.deviceId);
        }
        else {
            userSettingsStore.current.debugMode = false;
        }


        userSettingsStore.current.offlineMode = settings.offlineMode === true;
        userSettingsStore.current.monitorMode = settings.monitorMode === true;
        userSettingsStore.current.hideMenu = settings.hideMenu === true;
        userSettingsStore.current.scanDelay = settings.scanDelay;
        userSettingsStore.current.continusMode = settings.continusMode;
        userSettingsStore.current.invertDisplay = settings.invertDisplay;

        await userSettingsStore.saveAsync();

        operation.showAlert("Impostazioni salvate", "success", 4000);

        if (reload)
            window.location.href = window.location.pathname;
    }

    const generateLink = () => {

        const newParams = {
            pin: settings.pin,
            autosave: "1",
            apiUrl: settings.apiUrl,
            cameraEngine: settings.cameraEngine,
            debugMode: settings.debugMode ? "1" : undefined,
            enableScannerBLT: settings.enableScannerBLT ? "1" : undefined,
            offlineMode: settings.offlineMode ? "1": undefined,
            mode: "admin",
            scanDelay: settings.scanDelay?.toString(),
            logo: settings.logo,
            monitorMode: settings.monitorMode ? "1" : undefined,
            hideMenu: settings.hideMenu ? "1" : undefined,
        } as ISettingsParams;

        const newParamsQuery = new URLSearchParams();

        for (const key in newParams) {
            const value = newParams[key];
            if (value === null || value === undefined)
                continue;
            newParamsQuery.set(key, value);
        }


        const defaultPort = window.location.port == "80" || window.location.port == "443" || !window.location.port;

        let curUrl = window.location.protocol + "//" + window.location.hostname + (defaultPort ? "" : ":" + window.location.port) + window.location.pathname;
        curUrl += "#/settings?" + newParamsQuery.toString();

        if (navigator.clipboard) {
            navigator.clipboard.writeText(curUrl);
            operation.showAlert("Url copiata negli appunti", "success", 4000);
        }

        setSettingsCode("https://barcode-generator.boxol.it/qrcode/" + encodeURIComponent(curUrl) + "?no-cache=1");
    }

    const getImageBlob = async () => {

        return await (await fetch(settingsCode)).blob();
    }

    const copySettingsCode = async () => {
        const blob = await getImageBlob();

        if ("clipboard" in navigator) {
            const item = new ClipboardItem({ [blob.type]: blob });
            await navigator.clipboard.write([item]);
            operation.showAlert("QR Copiato", "success", 4000);
        }
    }

    const blobToBase64Async = (blob: Blob) => {

        return new Promise<string>(res => {
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result as string;
                const base64 = dataUrl.split(',')[1];
                res(base64);
            };
            reader.readAsDataURL(blob);

        });
     
    };

    const shareSettingsCode = async () => {
        const blob = await getImageBlob();

        if (isNativeDevice()) {
            window.nativeDevice.shareImage("Configurazione Gate Keeper", await blobToBase64Async(blob), "image/png");
        }
        else if ("share" in window.navigator) {
            const data: ShareData = {
                title: "Configurazione Gate Keeper",
                url: settingsCode,
                files: [new File([blob], "qrcode.png", { type: blob.type })]
            }

            if (!window.navigator.canShare(data))
                operation.showAlert("Impossibile condividere", "error", 2000);
            else
                await window.navigator.share(data)
        }
    }

    if (!settings)
        return null;

    const canShare = true;
    const canCopy = !isNativeDevice()
    const scanDelays = [0, 500, 1000, 2000]

    return (
        <Fragment>
            <header className="big" ref={attachPointer}>
                Settings
            </header>
            <div className="list">
                <div className="check">
                    <label>Scansione continua</label>
                    <Switch checked={settings.continusMode === true} onChange={(el, v) => setValue("continusMode", v, true)} />
                </div>
                <div className="text">
                    <FormControl>
                        <InputLabel variant="standard" id="camera-engine">Ritardo scansione multipla (ms)</InputLabel>
                    <Select variant="standard" label="Ritardo scansione multipla" value={settings.scanDelay} onChange={(el) => setValue("scanDelay", parseInt(el.target.value.toString()), true)}>
                        { scanDelays.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>) }
                        </Select>
                    </FormControl>
                </div>
                <div className="check">
                    <label>Modalità Monitor</label>
                    <Switch checked={settings.monitorMode === true} onChange={(el, v) => setValue("monitorMode", v, true)} />
                </div>
                <div className="check">
                    <label>Abilita offline</label>
                    <Switch checked={settings.offlineMode === true} onChange={(el, v) => setValue("offlineMode", v, true)} />
                </div>
                <div className="check">
                    <label>Nascondi menu</label>
                    <Switch checked={settings.hideMenu === true} onChange={(el, v) => setValue("hideMenu", v, true)} />
                </div>
                {isNativeDevice() && <div className="check">
                    <label>Schermo sottosopra</label>
                    <Switch checked={settings.invertDisplay === true} onChange={(el, v) => setValue("invertDisplay", v, true)} />
                </div>}
                {isAdmin || settings.debugMode ? <Fragment>
                    <div className="text">
                        <TextField value={settings.deviceId} onChange={ev => setValue("deviceId", ev.currentTarget.value, true)} label="Device Id" variant="standard" />
                    </div>
                    <div className="text">
                        <TextField value={settings.apiUrl} onChange={ev => setValue("apiUrl", ev.currentTarget.value)} label="API Endpoint" variant="standard" />
                    </div>
                    <div className="text">
                        <FormControl>
                            <InputLabel variant="standard" id="camera-engine">Camera Engine</InputLabel>
                            <Select labelId="camera-engine"
                                value={settings.cameraEngine}
                                    onChange={ev => setValue("cameraEngine", ev.target.value)}
                                    label="Camera Engine"
                                    variant="standard">
                                <MenuItem value="Cognex">Cognex</MenuItem>
                                <MenuItem value="ZBar">ZBar</MenuItem>
                                <MenuItem value="ZXing">ZXing</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className="check">
                        <label>Modalità Debug</label>
                        <Switch defaultChecked={settings.debugMode} onChange={(el, v) => setValue("debugMode", v)} />
                    </div>
                    <div className="text">
                        <TextField value={settings.pin} onChange={ev => setValue("pin", ev.currentTarget.value)} label="PIN" variant="standard" />
                    </div>
                    <div className="check">
                        <label>Scanner BLT</label>
                        <Switch defaultChecked={settings.enableScannerBLT} onChange={(el, v) => setValue("enableScannerBLT", v)} />
                    </div>
                    <div className="text">
                        <TextField type="number" value={settings.pingTimeout} onChange={ev => setValue("pingTimeout", parseInt(ev.currentTarget.value))} label="Ping Timeout (ms)" variant="standard" />
                    </div>
                    <div className="text">
                        <TextField type="number" value={settings.onlineCheckInterval / 1000} onChange={ev => setValue("onlineCheckInterval", parseInt(ev.currentTarget.value) * 1000)} label="Controllo online ogni (sec)" variant="standard" />
                    </div>
                    <div className="actions">
                        <Button className="save" variant="contained" onClick={() => saveSettingsAsync()}>Conferma</Button>
                        <Button className="generate-link" variant="contained" onClick={() => generateLink()}>Genera Link</Button>
                    </div>
                    {isNativeDevice() && <div className="actions">
                        <Button variant="contained" onClick={() => nativeDevice.showSettings()}>Android Settings</Button>
                    </div>}
                    {settingsCode && <div className="settings-code">
                        <img src={settingsCode} />
                        <div className="actions">
                            {canCopy && <Button variant="contained" onClick={() => copySettingsCode()}>Copia</Button>}
                            {canShare && <Button variant="contained" onClick={() => shareSettingsCode()}>Condividi</Button>}
                        </div>
                    </div>}
                </Fragment> : null}
            </div>
        </Fragment>
    );
}

const Settings: IPage = {

    name: "settings",
    title: "Settings",
    route: "/settings",
    icon: <SettingsIcon />,
    content: <SettingsContent />,
    container: Home,
    appBar: { backVisible: true }
};

registerPage(Settings);

export default Settings;